export type Provider = 'auto' | 'openai' | 'openrouter';


export function mapModelSelection(id: string): { provider?: Provider; model?: string } {
  const key = (id || 'auto').toLowerCase().trim();

  // Common shortcuts by keyword
  switch (key) {
    case 'auto':
      return { provider: 'auto', model: 'openai/gpt-5' };
    case 'chatgpt':
    case 'openai':
      return {
        provider: 'auto',
        // Prefer explicit env, fall back to a real, supported default
        model:
          process.env.NEXT_PUBLIC_OPENAI_MODEL ||
          process.env.NEXT_PUBLIC_CHATGPT5_MODEL ||
          'openai/gpt-5-chat',
      };
    case 'azure':
      return { provider: 'auto', model: 'openai/gpt-4o' };
    case 'claude':
    case 'anthropic':
      return { provider: 'openrouter', model: 'anthropic/claude-3.5-sonnet' };
    case 'deepseek':
      return { provider: 'openrouter', model: 'deepseek/deepseek-chat' };
    case 'gemini':
    case 'google':
      return {
        provider: 'openrouter',
        model: process.env.NEXT_PUBLIC_GEMINI25_MODEL || 'google/gemini-2.5-pro',
      };
    case 'meta':
      return { provider: 'auto', model: 'meta-llama/llama-4-scout' };
    case 'perplexity':
      return { provider: 'openrouter', model: 'perplexity/sonar' };
    case 'deepthink':
      return { provider: 'openrouter', model: 'deepthink/deepthink-r1' };
  }

  // If a full model id was passed (e.g., "openai/gpt-4o"), infer provider
  if (key.includes('/')) {
    const providerHint = key.split('/')[0];
    // Keep the model id exactly as selected
    const modelId = id;
    if (providerHint === 'meta') return { provider: 'auto', model: modelId };
    if (providerHint === 'openai' || providerHint === 'azure') return { provider: 'auto', model: modelId };
    // For other vendor prefixes, route via openrouter aggregator
    return { provider: 'openrouter', model: modelId };
  }

  // Final fallback (should rarely hit): use a stable model id
  return { provider: 'auto', model: 'openai/gpt-5' };
}

// Pretty display helpers for provider/model names (UI-facing only)
export function prettyProvider(p?: string | null): string {
  if (!p) return '';
  const key = String(p).trim().toLowerCase();
  switch (key) {
    case 'openai':
      return 'OpenAI';
    case 'azure':
      return 'Azure';
    case 'anthropic':
    case 'claude':
      return 'Anthropic';
    case 'google':
      return 'Google';
    case 'gemini':
      return 'Gemini';
    case 'xai':
      return 'xAI';
    case 'deepseek':
      return 'DeepSeek';
    case 'deepthink':
      return 'DeepThink';
    case 'openrouter':
      return 'OpenRouter';
    case 'auto':
      return 'Auto';
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
}

export function prettyModelId(modelId?: string | null): string {
  if (!modelId) return '';
  const id = String(modelId).trim();
  // Use the right half for vendor/model pairs
  const part = id.includes('/') ? id.split('/').slice(-1)[0] : id;
  // Capitalize certain families nicely (e.g., claude-3.5-haiku -> Claude-3.5-haiku)
  if (/^claude/i.test(part)) return 'Claude' + part.slice(6);
  if (/^gemini/i.test(part)) return 'Gemini' + part.slice(6);
  return part;
}
